/**
 * Structure fingerprints: the shape of compiled output without its words.
 *
 * What an edit's address depends on — node types, section refs and
 * numbering, list / row / option / column / cell counts, option codes,
 * column keys, signature panels, locks — plus the field references (value
 * `sourceKey`s, in order) a node prints. No text: a reworded paragraph
 * keeps its structure fingerprint, an added row or a removed field
 * reference does not.
 *
 * Used twice (ADR-0103): block versioning compares it per block across
 * versions (a structure change is a structural major), and the replay
 * compares a node's structure at edit time with its structure now to carry
 * a whole-text edit over a newer block version.
 */
import { canonicalJson } from '../document/revision.js';
import { hashString } from './fingerprint.js';
export const STRUCTURE_FINGERPRINT_PREFIX = 's1:';
/** The field keys an inline run prints, in order (`null` for an unbound value). */
export function inlineValueKeys(content) {
    const out = [];
    const visit = (nodes) => {
        for (const n of nodes) {
            if (n.type === 'value')
                out.push(n.sourceKey ?? null);
            else if (n.type === 'strong' || n.type === 'emphasis' || n.type === 'label')
                visit(n.content);
        }
    };
    visit(content ?? []);
    return out;
}
const live = (list) => list.filter((n) => n.edit?.change !== 'removed');
/** The text-free shape of one node (and its subtree). */
export function structureShape(node) {
    const kids = (list) => live(list).map(structureShape);
    const lock = node.locked ? { locked: true } : {};
    switch (node.type) {
        case 'section':
            return {
                t: 'section',
                ref: node.ref,
                numbering: node.numbering ?? 'auto',
                ...(node.numbering === 'fixed' ? { number: node.number ?? null } : {}),
                title: node.title != null,
                ...lock,
                c: kids(node.content),
            };
        case 'heading':
        case 'paragraph':
            return { t: node.type, v: inlineValueKeys(node.content), ...lock };
        case 'list':
            return { t: 'list', ...lock, items: node.items.map((i) => kids(i.content)) };
        case 'optionList':
            return {
                t: 'optionList',
                sourceKey: node.sourceKey ?? null,
                select: node.select ?? null,
                ...lock,
                items: node.items.map((i) => ({ code: i.code ?? null, ...(i.sourceKey ? { k: i.sourceKey } : {}), v: inlineValueKeys(i.content), c: kids(i.children ?? []) })),
            };
        case 'table':
            return {
                t: 'table',
                ...lock,
                columns: node.columns.map((c) => ({ key: c.key, header: c.header != null, v: inlineValueKeys(c.header) })),
                rows: node.rows.map((r) => r.cells.map((c) => kids(c.content))),
                caption: node.caption != null,
                cv: inlineValueKeys(node.caption),
            };
        case 'detailList':
            return {
                t: 'detailList',
                ...lock,
                items: node.items
                    .filter((i) => i.edit?.change !== 'removed')
                    .map((i) => ({ v: inlineValueKeys(i.term), d: kids(i.description) })),
            };
        case 'signature':
            return {
                t: 'signature',
                v: inlineValueKeys(node.statement),
                panels: node.panels.map((p) => ({ partyRef: p.partyRef ?? null, role: p.role ?? null, ordinal: p.ordinal ?? null, anchor: p.anchor ?? null })),
            };
        case 'notice':
        case 'group':
            return { t: node.type, ...lock, c: kids(node.content) };
        default:
            return { t: node.type, ...lock };
    }
}
/** Structure fingerprint of one node (deep — a section includes its content). */
export const fingerprintNodeStructure = (node) => STRUCTURE_FINGERPRINT_PREFIX + hashString(canonicalJson(structureShape(node)));
/** Structure fingerprint of a block instance's top-level output. */
export const fingerprintNodesStructure = (nodes) => STRUCTURE_FINGERPRINT_PREFIX + hashString(canonicalJson(live(nodes).map(structureShape)));
/** Structure fingerprint of a part (row / option label / column header / caption); `null` when absent. */
export function fingerprintPartStructure(owner, part) {
    let shape;
    switch (part.kind) {
        case 'row': {
            const item = owner.type === 'detailList' ? owner.items[part.index] : undefined;
            shape = item ? { t: 'detailRow', v: inlineValueKeys(item.term), d: live(item.description).map(structureShape) } : undefined;
            break;
        }
        case 'option': {
            const item = owner.type === 'optionList' ? owner.items[part.index] : undefined;
            shape = item ? { t: 'optionLabel', code: item.code ?? null, v: inlineValueKeys(item.content) } : undefined;
            break;
        }
        case 'columnHeader': {
            const column = owner.type === 'table' ? owner.columns[part.index] : undefined;
            shape = column ? { t: 'columnHeader', key: column.key, v: inlineValueKeys(column.header) } : undefined;
            break;
        }
        case 'caption':
            shape = owner.type === 'table' && owner.caption ? { t: 'caption', v: inlineValueKeys(owner.caption) } : undefined;
            break;
    }
    if (shape === undefined)
        return null;
    const locked = owner.locked ? { locked: true } : {};
    return STRUCTURE_FINGERPRINT_PREFIX + hashString(canonicalJson({ ...shape, ...locked }));
}
//# sourceMappingURL=structure.js.map